import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AdminNavBar from "../components/AdminNavBar";
import { AdminPageBody, AdminLoadingState, AdminEmptyState } from "../components/AdminPageShell";
import Toast from "../components/Toast";
import { useAdminAcademicResources } from "../store/admin";
import { htmlToText } from "../utils/toDisplayString";
import {
  ArrowLeft,
  CheckCircle2,
  XCircle,
  FileDown,
  BookOpen,
  Book,
  FileText,
  User,
  Tag,
  Wallet,
  Download,
  Calendar,
  Eye,
  Pencil,
} from "lucide-react";

/** Singular kind -> plural endpoint segment, label and icon. */
const KIND_META = {
  book: { plural: "books", label: "Book", icon: Book },
  course: { plural: "courses", label: "Course", icon: BookOpen },
  journal: { plural: "journals", label: "Journal", icon: FileText },
};

/** Collapse API status values into the same buckets used by the list page. */
function mapStatus(raw) {
  const s = String(raw || "APPROVED").toUpperCase();
  if (s === "DRAFT") return "Draft";
  if (["PENDING", "PENDING_REVIEW", "UNDER_REVIEW"].includes(s)) return "Pending";
  if (["REJECTED", "DISABLED", "ARCHIVED", "SUSPENDED", "WITHDRAWN"].includes(s)) return "Archived";
  if (["APPROVED", "ACTIVE", "PUBLISHED"].includes(s)) return "Active";
  return s.charAt(0) + s.slice(1).toLowerCase();
}

const statusStyles = {
  Active: "bg-green-100 text-green-700",
  Pending: "bg-[#dae2fd] text-[#565e74]",
  Draft: "bg-slate-100 text-slate-600",
  Archived: "bg-[#e0e3e5] text-[#434655]",
};

const formatMoney = (value) => {
  const n = Number(value);
  return Number.isNaN(n) || value == null ? "Free" : `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};

const formatDate = (value) => (value ? new Date(value).toLocaleString() : "—");

// ===== ACADEMIC RESOURCE DETAIL — GET /api/admin/{books|courses|journals}/:id =====
// Full review of a single book / course / journal before the admin approves
// or rejects it. The kind comes from the route param so one page serves all
// three resource types.
const AcademicResourceDetail = () => {
  const { kind, resourceId } = useParams();
  const navigate = useNavigate();

  const meta = KIND_META[kind];
  const plural = meta?.plural;

  const {
    bookDetail,
    bookDetailLoading,
    bookDetailError,
    fetchBook,
    courseDetail,
    courseDetailLoading,
    courseDetailError,
    fetchCourse,
    journalDetail,
    journalDetailLoading,
    journalDetailError,
    fetchJournal,
    approveBook,
    rejectBook,
    approveCourse,
    rejectCourse,
    disableCourse,
    featureCourse,
    unfeatureCourse,
    deleteCourse,
    approveJournal,
    rejectJournal,
    academicMutationLoading,
  } = useAdminAcademicResources();

  const [loaded, setLoaded] = useState(false);
  const [actionError, setActionError] = useState("");
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState("");
  const [toast, setToast] = useState(null);
  const [toastSuccess, setToastSuccess] = useState(true);

  // Pick the detail + fetcher + moderation actions for the current kind.
  const config = {
    books: { detail: bookDetail, loading: bookDetailLoading, error: bookDetailError, fetch: fetchBook, approve: approveBook, reject: rejectBook },
    courses: { detail: courseDetail, loading: courseDetailLoading, error: courseDetailError, fetch: fetchCourse, approve: approveCourse, reject: rejectCourse, disable: disableCourse, feature: featureCourse, unfeature: unfeatureCourse, delete: deleteCourse },
    journals: { detail: journalDetail, loading: journalDetailLoading, error: journalDetailError, fetch: fetchJournal, approve: approveJournal, reject: rejectJournal },
  }[plural] || {};

  // GET /api/admin/<plural>/:id
  useEffect(() => {
    if (plural && resourceId && config.fetch) {
      config.fetch(resourceId).finally(() => setLoaded(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plural, resourceId]);

  const backPath = "/admin/academic-resources";
  const goBack = () => navigate(backPath);

  const detail = config.detail;
  const status = detail ? mapStatus(detail.status) : "Pending";
  const TypeIcon = meta?.icon || BookOpen;
  const cover = detail ? detail.coverImage || detail.image || detail.coverImageUrl || null : null;

  // Concise header facts (rendered as chips under the title).
  const headerChips = detail
    ? [
        { icon: Tag, label: "Category", value: detail.category?.name || detail.categoryName || "—" },
        {
          icon: User,
          label: kind === "course" ? "Tutor" : "Author",
          value:
            detail.tutor?.name ||
            detail.author ||
            detail.createdBy?.name ||
            detail.creator?.name ||
            detail.creatorName ||
            "—",
        },
        { icon: Wallet, label: "Price", value: formatMoney(detail.price) },
        {
          icon: Download,
          label: kind === "course" ? "Enrollments" : "Downloads",
          value: detail.enrollmentCount ?? detail.downloads ?? detail.downloadCount ?? "0",
        },
        { icon: Calendar, label: kind === "course" ? "Uploaded" : "Published", value: formatDate(detail.uploadedAt || detail.createdAt) },
      ]
    : [];

  // Remaining detail rows (excluding the ones already surfaced as chips).
  const detailRows = detail
    ? [
        ["Status", status],
        ["Description", htmlToText(detail.description || detail.introduction) || "No description provided."],
        ["Featured", detail.isFeatured ? "Yes" : "No"],
        ["Last updated", formatDate(detail.updatedAt)],
        ...(detail.submittedAt ? [["Submitted at", formatDate(detail.submittedAt)]] : []),
        ...(detail.adminModerator ? [["Admin moderator", detail.adminModerator.name || detail.adminModerator.email || "—"]] : []),
        ...(detail.staffModerator ? [["Staff moderator", detail.staffModerator.name || detail.staffModerator.email || "—"]] : []),
        ...(detail.fileUrl || detail.file ? [["File", detail.fileUrl || detail.file]] : []),
        ...(detail.rejectionReason ? [["Rejection reason", detail.rejectionReason]] : []),
      ]
    : [];

  const showToast = (message, success = true) => {
    setToast(message);
    setToastSuccess(success);
    window.setTimeout(() => setToast(null), 5000);
  };

  const runAction = async (action, successMessage) => {
    setActionError("");
    try {
      const result = await action();
      if (!result?.success) {
        const message = result?.error || "Action failed";
        setActionError(message);
        showToast(message, false);
        return false;
      }
      // The store patches the detail immediately; refetch in the background
      // only to pick up any server-computed fields.
      config.fetch?.(resourceId);
      showToast(successMessage, true);
      return true;
    } catch (error) {
      const message = error?.message || "Action failed";
      setActionError(message);
      showToast(message, false);
      return false;
    }
  };

  const handleApprove = () =>
    runAction(() => config.approve(resourceId), `${meta?.label || "Resource"} approved`);

  const submitReject = async (event) => {
    event.preventDefault();
    const ok = await runAction(
      () => config.reject(resourceId, reason.trim()),
      `${meta?.label || "Resource"} rejected`
    );
    if (ok) {
      setRejecting(false);
      setReason("");
    }
  };

  const handleCourseControl = (action, successMessage) =>
    runAction(() => action(resourceId), successMessage);

  const handleDeleteCourse = async () => {
    if (!window.confirm("Delete this course permanently? This action cannot be undone.")) return;
    const deleted = await runAction(() => config.delete(resourceId), "Course deleted successfully");
    if (deleted) navigate(backPath, { replace: true });
  };

  const errorMessage =
    actionError || config.error || (meta ? null : `Unknown resource type "${kind}".`);




  return (
    <div>
      <AdminNavBar
        title="Academic Resources"
        subTitle={detail?.title || meta?.label || "Details"}
        context="Review the full submission before approving or rejecting it."
      />
      <AdminPageBody>
        {toast && (
          <Toast message={toast} isSuccess={toastSuccess} onClose={() => setToast(null)} />
        )}
        <div>
          <button
            onClick={goBack}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-medium text-slate-600 shadow-sm transition hover:bg-slate-50"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Academic Resources
          </button>
        </div>

        {errorMessage && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-600">
            {errorMessage}
          </div>
        )}

        {!meta ? (
          <AdminEmptyState title="Unknown resource type" description={`No detail page for "${kind}".`} />
        ) : config.loading || (!loaded && !config.error) ? (
          <AdminLoadingState />
        ) : config.error && !detail ? (
          <AdminEmptyState title={`${meta.label} not found`} description={config.error} />
        ) : detail ? (
          <>
            {/* ===== COVER BANNER ===== */}
            <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-slate-100 shadow-sm">
              {cover ? (
                <img
                  src={cover}
                  alt={detail.title}
                  className="w-full h-64 sm:h-80 lg:h-96 object-cover"
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <div className="w-full h-64 sm:h-80 lg:h-96 flex flex-col items-center justify-center gap-3 bg-gradient-to-br from-slate-200 to-slate-100">
                  <TypeIcon className="w-16 h-16 text-slate-400" />
                  <span className="text-sm text-slate-500">No cover image provided</span>
                </div>
              )}
              {/* Legibility gradient over the cover */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/25 to-transparent pointer-events-none" />

              {/* Title + status pinned to the bottom of the cover */}
              <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8 flex flex-wrap items-end justify-between gap-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-white/90 px-2.5 py-1 text-xs font-semibold text-slate-700">
                      <TypeIcon className="w-3.5 h-3.5" /> {meta.label}
                    </span>
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-bold uppercase ${
                        statusStyles[status] || statusStyles.Pending
                      }`}
                    >
                      {status}
                    </span>
                  </div>
                  <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white drop-shadow">
                    {detail.title || detail.name || "Untitled"}
                  </h1>
                </div>
                <div className="text-white text-right">
                  <p className="text-xs uppercase tracking-wider opacity-80">Price</p>
                  <p className="text-2xl font-bold">{formatMoney(detail.price)}</p>
                </div>
              </div>
            </div>

            {/* ===== DECISION BAR ===== */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                  <h2 className="text-base font-semibold text-slate-900">Moderation decision</h2>
                  <p className="text-sm text-slate-500">
                    {status === "Draft"
                      ? `This ${meta.label.toLowerCase()} is still a draft. It must be submitted for review before it can be approved.`
                      : status === "Pending"
                      ? `Approve this ${meta.label.toLowerCase()} to publish it, or reject it with a reason.`
                      : status === "Active"
                        ? `This ${meta.label.toLowerCase()} has already been approved. Approval and rejection are mutually exclusive, so it can no longer be rejected.`
                        : `This ${meta.label.toLowerCase()} has already been rejected. Approval and rejection are mutually exclusive, so it can no longer be approved.`}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/resources/${kind}/${resourceId}/edit`)}
                    className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                  >
                    <Pencil className="h-4 w-4" /> Edit details
                  </button>
                  <button
                    type="button"
                    onClick={handleApprove}
                    disabled={academicMutationLoading || status !== "Pending"}
                    title={status !== "Pending" ? "Only pending resources can be approved." : undefined}
                    className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-green-600 bg-green-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {academicMutationLoading ? (
                      "Working…"
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4" /> Approve
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={() => setRejecting((v) => !v)}
                    disabled={academicMutationLoading || status !== "Pending"}
                    title={status !== "Pending" ? "Only pending resources can be rejected." : undefined}
                    className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-red-600 bg-white px-5 py-2.5 text-sm font-semibold text-red-600 shadow-sm transition hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <XCircle className="w-4 h-4" /> Reject
                  </button>
                </div>
              </div>

              {rejecting && (
                <form onSubmit={submitReject} className="mt-5 border-t border-slate-100 pt-5">
                  <label htmlFor="reject-reason" className="block text-sm font-semibold text-slate-700 mb-1">
                    Reason for rejection
                  </label>
                  <textarea
                    id="reject-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                    placeholder={`e.g. Missing references for this ${meta.label.toLowerCase()}…`}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-red-400 focus:ring-2 focus:ring-red-100 outline-none"
                  />
                  <div className="mt-3 flex items-center gap-2">
                    <button
                      type="submit"
                      disabled={academicMutationLoading || status !== "Pending" || !reason.trim()}
                      className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-red-600 bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Confirm rejection
                    </button>
                    <button
                      type="button"
                      onClick={() => setRejecting(false)}
                      className="inline-flex min-h-10 items-center justify-center rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              )}
            </div>

            {plural === "courses" && detail ? (
              <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                <h2 className="m-0 text-base font-semibold text-slate-900">Course controls</h2>
                <p className="m-0 mt-1 text-sm text-slate-500">
                  Feature, unfeature, or disable this course.
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={academicMutationLoading}
                    onClick={() => handleCourseControl(config.feature, "Course featured")}
                    className="inline-flex min-h-10 items-center justify-center rounded-lg border border-amber-300 bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-800 transition hover:bg-amber-100 disabled:opacity-50"
                  >
                    Feature
                  </button>
                  <button
                    type="button"
                    disabled={academicMutationLoading}
                    onClick={() => handleCourseControl(config.unfeature, "Course unfeatured")}
                    className="inline-flex min-h-10 items-center justify-center rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
                  >
                    Unfeature
                  </button>
                  <button
                    type="button"
                    disabled={academicMutationLoading}
                    onClick={() => handleCourseControl(config.disable, "Course disabled")}
                    className="inline-flex min-h-10 items-center justify-center rounded-lg border border-red-300 bg-red-50 px-4 py-2 text-sm font-semibold text-red-700 transition hover:bg-red-100 disabled:opacity-50"
                  >
                    Disable
                  </button>
                  <button
                    type="button"
                    disabled={academicMutationLoading}
                    onClick={handleDeleteCourse}
                    className="inline-flex min-h-10 items-center justify-center rounded-lg border border-red-700 bg-red-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-800 disabled:opacity-50"
                  >
                    Delete course
                  </button>
                </div>
              </div>
            ) : null}

            {/* ===== QUICK FACTS ===== */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {headerChips.map((chip) => (
                <div key={chip.label} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                  <div className="flex items-center gap-2 text-slate-500 mb-1">
                    <chip.icon className="w-4 h-4" />
                    <span className="text-[11px] uppercase tracking-wider font-semibold">{chip.label}</span>
                  </div>
                  <p className="text-sm font-semibold text-slate-900 truncate">{chip.value}</p>
                </div>
              ))}
            </div>

            {/* ===== DETAILS ===== */}
            <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <div className="flex items-center gap-2 mb-4">
                <Eye className="w-4 h-4 text-slate-400" />
                <h3 className="text-base font-semibold text-slate-900">Details</h3>
              </div>
              <dl className="divide-y divide-slate-100">
                {detailRows.map(([label, value]) => (
                  <div key={label} className="grid grid-cols-1 sm:grid-cols-3 gap-2 py-3">
                    <dt className="text-sm font-medium text-slate-500">{label}</dt>
                    <dd className="sm:col-span-2 text-sm text-slate-900">
                      {label === "Status" ? (
                        <span
                          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                            statusStyles[status] || statusStyles.Pending
                          }`}
                        >
                          {value}
                        </span>
                      ) : label === "File" && value && value !== "—" ? (
                        <a
                          href={value}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 text-[#004ac6] underline"
                        >
                          <FileDown className="w-4 h-4" /> Open file
                        </a>
                      ) : (
                        value
                      )}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>

            <div className="flex justify-end">
              <button
                type="button"
                onClick={goBack}
                className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg bg-[#004ac6] px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-[#003ba0]"
              >
                Done — back to list
              </button>
            </div>
          </>
        ) : null}
      </AdminPageBody>
    </div>
  );
};

export default AcademicResourceDetail;
